import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Row, Col, Tooltip, Tag, Button } from 'antd';
import { InfoCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { diff } from 'jsondiffpatch';
import SpeechBubble from './SpeechBubble';

/**
 * Represents the overall structure of differences between two JSON objects.
 * - missingProperties: An array of property keys that exist in the "expected" but not in the "model".
 * - unequalProperties: An array of property keys that differ between "expected" and "model".
 * - total: The total number of differences (optional, but often used for summary).
 */
interface DifferenceList {
  missingProperties: string[];
  unequalProperties: string[];
  total: number;
}

/**
 * Props accepted by the JsonDiffViewer component.
 */
interface JsonDiffViewerProps {
  /** The user's original utterance. */
  userUtterance: string;
  /** The canonical version of the utterance (if any). */
  canonical?: string;
  /** An array of paraphrased utterances (if any). */
  paraphrases?: string[];
  /** Any associated labels to display, such as "Intent" or "Category". */
  labels?: string[];

  /** The "gold standard" JSON we expect. */
  expectedJson: object;
  /** The JSON string generated by a model or service. */
  modelJson: string;
  /** A structured list of differences between expected and model JSON. */
  differenceList?: DifferenceList;

  /** Titles to render above each column for context. */
  expectedTitle: string;
  modelTitle: string;
  differenceTitle: string;

  /** Callback function, e.g. to close this viewer. */
  onClose: () => void;
  
  /** Whether this is synchronized with global run selection */
  isSynchronized?: boolean;
  /** Callback for run navigation */
  onRunNavigation?: (direction: 'prev' | 'next') => void;
  /** Current run index from global state */
  currentRunIndex: number | null;
  /** Total number of runs */
  totalRuns?: number;
  /** Whether to show the diff button (hidden when showing averaged) */
  showDiffButton?: boolean;
}

/**
 * Utility function to produce valid HTML IDs from a string.
 * Replaces any character outside [a-zA-Z0-9_-] with a dash.
 */
function sanitizeForId(str: string) {
  return str.replace(/[^a-zA-Z0-9_-]/g, '-');
}

/**
 * Assigns a deterministic color to a label based on its characters.
 * Helps ensure the same label is always the same color.
 */
function getColorForLabel(label: string) {
  const colors = [
    'magenta', 'red', 'volcano', 'orange', 'gold',
    'lime', 'green', 'cyan', 'blue', 'geekblue', 'purple',
  ];
  // Sum up character codes in the label, then mod by the color array length.
  const index = Math.abs(
    label.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  ) % colors.length;
  return colors[index];
}

/**
 * Recursively walk a jsondiffpatch “diff” object and collect every path
 * at which a primitive value changed. For example, if you see:
 *
 *   diffObj = {
 *     fields: {
 *       0: { aggregation: ["sum", null, 0] },
 *       1: { type: ["continuous", "discrete", 0] }
 *     },
 *     version: ["0.1.0", "0.2.0", 0]
 *   }
 *
 * This function will push “fields.0.aggregation”, “fields.1.type”, and “version”.
 */
function extractDiffPaths(
  diffObj: any,
  currentPath: string,
  out: string[]
) {
  // jsondiffpatch marks array changes as arrays of length≥2, or nested objects
  if (typeof diffObj !== 'object' || diffObj === null) {
    return;
  }

  Object.keys(diffObj).forEach((key) => {
    if (key === '_t') {
      // jsondiffpatch uses “_t” to mark array diffs—skip it
      return;
    }

    const val = diffObj[key];
    const newPath = currentPath ? `${currentPath}.${key}` : key;

    // If val is an array of length ≥ 2 and both entries are primitives (old, new),
    // it’s a leaf “change”
    if (
      Array.isArray(val) &&
      val.length >= 2 &&
      (typeof val[0] !== 'object' || val[0] === null) &&
      (typeof val[1] !== 'object' || val[1] === null)
    ) {
      out.push(newPath);
      return;
    }

    // Otherwise, it might be a nested object: dive in
    if (typeof val === 'object' && val !== null) {
      extractDiffPaths(val, newPath, out);
    }
  });
}

/**
 * Recursively render a JSON value as HTML, injecting <span id="prefix-path"> around any
 * property whose “fullPath” (e.g. "fields.0.aggregation") matches one of the `paths` of interest.
 *
 * Returns a long HTML string with proper indentation, <br> for newlines, and <span> wrappers
 * only around the exact key:value pairs at the nested paths in `paths`.
 */
function renderJsonToHtml(
  obj: any,
  paths: string[],
  prefix: 'expected' | 'model',
  currentPath = '',
  indent = 0
): string {
  const indentStr = ' '.repeat(indent * 2);
  function escapeHtml(s: string) {
    return s.replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
  }

  if (obj === null || typeof obj !== 'object') {
    return escapeHtml(JSON.stringify(obj));
  }

  if (Array.isArray(obj)) {
    let html = '[<br>';
    for (let i = 0; i < obj.length; i++) {
      const childPath = currentPath ? `${currentPath}.${i}` : `${i}`;
      html += indentStr + '  ' + renderJsonToHtml(obj[i], paths, prefix, childPath, indent + 1);
      if (i < obj.length - 1) html += ',';
      html += '<br>';
    }
    html += indentStr + ']';
    return html;
  }

    const keys = Object.keys(obj);
    const bodyHtml = keys
      .map((key, idx) => {
        const childPath = currentPath ? `${currentPath}.${key}` : key;
        const isLeaf = paths.includes(childPath);

        // Build the opening span, injecting an id if it’s a diff leaf:
        const spanAttrs = isLeaf
          ? ` id="${prefix}-${sanitizeForId(childPath)}"`
          : '';
        const keyPart = `${indentStr}  "<span${spanAttrs}">`
                      + `${escapeHtml(key)}</span>": `;

        // Recursively render the value
        const valPart = renderJsonToHtml(
          obj[key],
          paths,
          prefix,
          childPath,
          indent + 1
        );

        // Append a comma only if not the last key
        const comma = idx < keys.length - 1 ? ',' : '';
        return `${keyPart}${valPart}${comma}`;
      })
      .join('<br>');

    // Wrap it all in braces:
    return `{\n${bodyHtml}\n${indentStr}}`;
}

/**
 * Given a notional‐spec object, produce a copy where any top‐level "fields"
 * array is sorted by its "caption" property. This prevents index‐based diffs
 * when two field objects have merely swapped positions.
 */
function normalizeFieldsArray(input: any): any {
 // If there's no top‐level "fields" key or it's not an array, just return input as is.
  if (!input || typeof input !== 'object' || !Array.isArray((input as any).fields)) {
    return input;
  }

  // Shallow‐copy the object so we don't mutate the original
  const copy: any = { ...input };
  // Copy the "fields" array and sort by caption (falling back to empty string).
  copy.fields = [...copy.fields].sort((a: any, b: any) => {
    const aCap = typeof a.caption === 'string' ? a.caption : '';
    const bCap = typeof b.caption === 'string' ? b.caption : '';
    return aCap.localeCompare(bCap);
  });

  return copy;
}



/**
 * Component that visually compares two JSON objects ("expected" vs. "model") and
 * highlights the differences. It also provides a list of differences that can be
 * navigated (prev/next), synchronizing scrolling and highlighting in the JSON panels.
 */
export const JsonDiffViewer: React.FC<JsonDiffViewerProps> = ({
  userUtterance,
  canonical,
  paraphrases,
  labels,
  expectedJson,
  modelJson,
  differenceList,
  expectedTitle,
  modelTitle,
  differenceTitle,
  onClose,
  isSynchronized = false,
  onRunNavigation,
      currentRunIndex = null,
  totalRuns,
  showDiffButton = true,
}) => {
  // Tracks which difference is currently selected in the right-hand list.
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Refs to the DOM containers for the "expected" JSON and "model" JSON.
  // Used for scrolling to highlighted items.
  const expectedRef = useRef<HTMLDivElement>(null);
  const modelRef = useRef<HTMLDivElement>(null);

  // We store the previously selected <span> elements, so we can easily reset
  // their backgrounds (instead of resetting all differences every time).
  const previousSelectedExpected = useRef<HTMLSpanElement | null>(null);
  const previousSelectedModel = useRef<HTMLSpanElement | null>(null);

  // Safely parse the model JSON string. If it's invalid, fall back to an empty object.
  let parsedModelJson: any;
  try {
    parsedModelJson = JSON.parse(modelJson);
  } catch (err) {
    parsedModelJson = {};
  }
    // Step 1: Normalize (i.e. sort) any top‐level `fields` arrays by "caption".
  // This ensures swapped‐positions inside "fields" do not produce index‐based diffs.
  const normalizedExpected = normalizeFieldsArray(expectedJson);
  const normalizedModel    = normalizeFieldsArray(parsedModelJson);

  // Step 2: Produce a structured diff on the normalized objects.
  const modelDiff = diff(normalizedExpected, normalizedModel);

  /**
   * Recursively extract all nested paths of primitive‐value changes from the jsondiffpatch diff.
   * E.g. if diff indicates changed["fields"][1]["aggregation"], we record "fields.1.aggregation".
   */
  const nestedPaths: string[] = [];
  if (modelDiff) {
    extractDiffPaths(modelDiff, '', nestedPaths);
 }

  // If no paths were found, log a warning
  if (!nestedPaths.length) {
    console.warn('No differences detected.');
 }

  // mergedDiffs now holds every changed path (including nested ones)
  const mergedDiffs = nestedPaths;

  /**
   * Decide what text to show in the speech bubble (left side).
   * Priority order: paraphrase -> canonical -> userUtterance.
   * We also control a tooltip if the paraphrase differs from the canonical.
   */
  const paraphrase = paraphrases && paraphrases.length > 0 ? paraphrases[0] : null;
  let textInBubble = '';
  let showTooltip = false;

  if (paraphrase && canonical) {
    if (paraphrase === canonical) {
      textInBubble = paraphrase;
      showTooltip = false;
    } else {
      textInBubble = paraphrase;
      showTooltip = true;
    }
  } else if (paraphrase) {
    textInBubble = paraphrase;
  } else if (canonical) {
    textInBubble = canonical;
  } else {
    textInBubble = userUtterance || '';
  }

  // Produce the final HTML strings for "expected" and "model" JSON with embedded <span> tags
  const expectedStr = renderJsonToHtml(expectedJson, mergedDiffs, 'expected');
  const modelStr = renderJsonToHtml(parsedModelJson, mergedDiffs, 'model');
  /**
   * When the user clicks on a difference in the right-hand list, we update
   * the selectedIndex state, triggering our highlighting useEffect.
   */
  function handleDiffClick(i: number) {
    setSelectedIndex(i);
  }

  /**
   * useMemo for caching the DOM queries:
   * We only query each diff key's <span> once per `mergedDiffs` update,
   * rather than on every render. This can reduce overhead for large diff sets.
   */
  const highlightElements = useMemo(() => {
    // If refs aren't ready, just return an empty list.
    if (!expectedRef.current || !modelRef.current) return [];

    return mergedDiffs.map((diffKey) => {
      const sanitized = sanitizeForId(diffKey);
      const expectedElem = expectedRef.current?.querySelector<HTMLSpanElement>(
        `#expected-${sanitized}`
      );
      const modelElem = modelRef.current?.querySelector<HTMLSpanElement>(
        `#model-${sanitized}`
      );
      return { diffKey, expectedElem, modelElem };
    });
  }, [mergedDiffs, expectedRef, modelRef]);

  /**
   * useEffect #1:
   * Initialize all difference spans to light grey whenever `highlightElements` changes.
   * This typically runs after `mergedDiffs` is updated or if the panel refs change.
   */
  useEffect(() => {
    highlightElements.forEach(({ expectedElem, modelElem }) => {
      if (expectedElem) expectedElem.style.backgroundColor = 'lightgrey'; 
      if (modelElem)    modelElem .style.backgroundColor = 'lightgrey';
    });
  }, [highlightElements]);

  /**
   * useEffect #2:
   * Whenever `selectedIndex` changes, reset the previously selected elements (if any)
   * back to light grey, then highlight the newly selected elements in yellow,
   * and optionally scroll them into view.
   */
  useEffect(() => {
    // Reset old highlight
    if (previousSelectedExpected.current) {
      previousSelectedExpected.current.style.backgroundColor = 'lightgrey';
    }
    if (previousSelectedModel.current) {
      previousSelectedModel.current.style.backgroundColor = 'lightgrey';
    }

    // Highlight new selection (if valid)
    if (selectedIndex !== null && highlightElements[selectedIndex]) {
      const { expectedElem, modelElem } = highlightElements[selectedIndex];

      if (expectedElem) {
        expectedElem.style.backgroundColor = '#ffe58f';
        /* snap to the *start* edge so scroll-snap can take over */
         expectedElem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        previousSelectedExpected.current = expectedElem;
      } else {
        previousSelectedExpected.current = null;
      }

      if (modelElem) {
        modelElem.style.backgroundColor = '#ffe58f';
        modelElem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        previousSelectedModel.current = modelElem;
      } else {
        previousSelectedModel.current = null;
      }
    }
  }, [selectedIndex, highlightElements]);

  /**
   * Handlers for the "Previous" / "Next" buttons in the Differences column.
   * These allow the user to cycle through the array of differences.
   */
  function handlePrev() {
    if (selectedIndex === null || selectedIndex <= 0) return;
    setSelectedIndex((prev) => (prev === null ? 0 : prev - 1));
  }

  function handleNext() {
    if (selectedIndex === null) {
      // If none is selected yet, start at the first difference.
      setSelectedIndex(0);
      return;
    }
    if (selectedIndex < mergedDiffs.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  }

  /**
   * Common styles for the JSON panels (Expected/Model).
   * We extract them into a single object for code clarity.
   */
  const jsonPanelStyle: React.CSSProperties = {
    maxHeight: 400,
    overflow: 'auto',
    /* enable gentle “magnetic” snapping between highlighted spans */
    scrollSnapType: 'y proximity',
    backgroundColor: '#f5f5f5',
    padding: 10,
    borderRadius: 4,
    fontFamily: 'monospace',
    fontSize: 14,
    lineHeight: '1.5',
    marginTop: 8,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };

  return (
    <>
      {/* 
        The top section: shows a speech bubble with the user's text (or paraphrase/canonical).
        Optionally displays a tooltip if a paraphrase differs from its canonical.
        Also shows any labels (e.g. "Intent", "Category") as colored tags.
        Also shows runs navigator when in synchronized mode.
      */}
      <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <SpeechBubble variant="user">{textInBubble}</SpeechBubble>
            {showTooltip && canonical && (
              <Tooltip title={`Canonical: ${canonical}`}>
                <InfoCircleOutlined style={{ marginLeft: 8, cursor: 'pointer' }} />
              </Tooltip>
            )}
          </div>
          
          {/* ——— Runs Navigator next to user utterance ——— */}
          {totalRuns && totalRuns > 1 && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 4, 
              justifyContent: 'flex-start'
            }}>
              {isSynchronized && currentRunIndex !== null ? (
                <>
                  <Button
                    size="small"
                    icon={<LeftOutlined />}
                    disabled={currentRunIndex === 0}
                    onClick={() => {
                      if (onRunNavigation) {
                        onRunNavigation('prev');
                      }
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#666' }}>
                    {`${currentRunIndex + 1}/${totalRuns}`}
                  </span>
                  <Button
                    size="small"
                    icon={<RightOutlined />}
                    disabled={currentRunIndex === (totalRuns - 1)}
                    onClick={() => {
                      if (onRunNavigation) {
                        onRunNavigation('next');
                      }
                    }}
                  />
                </>
              ) : (
                <span style={{ fontSize: '11px', color: '#666' }}>
                  Averaged Runs
                </span>
              )}
            </div>
          )}
        </div>
        {labels && labels.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {labels.map((label) => (
              <Tag key={label} color={getColorForLabel(label)} style={{ marginRight: 4 }}>
                {label}
              </Tag>
            ))}
          </div>
        )}
      </div>
      {/* 
        Three-column layout:
          1) Expected JSON
          2) Model JSON
          3) Differences / navigation
      */}
      <Row gutter={16}>
        {/* Expected Column */}
        <Col span={8}>
          <h3>{expectedTitle}</h3>
          <div
            ref={expectedRef}
            style={jsonPanelStyle}
            // Render our recursively‐generated HTML with <span> tags
            dangerouslySetInnerHTML={{ __html: expectedStr }}
          />
        </Col>

        {/* Model Column */}
        <Col span={8}>
          <h3>{modelTitle}</h3>
          <div
            ref={modelRef}
            style={jsonPanelStyle}
            dangerouslySetInnerHTML={{ __html: modelStr }}
          />
        </Col>

        {/* Differences Column */}
        <Col span={8}>
          <h3>{differenceTitle}</h3>
          {mergedDiffs.length === 0 ? (
            // If no differences at all, show a simple message.
            <div>No differences found.</div>
          ) : (
            <>
              {/* 
                Render each difference as an item in a list.
                Clicking one updates the selectedIndex, which highlights and scrolls
                the corresponding JSON key in both Expected and Model panels.
              */}
              <ul
                style={{
                  maxHeight: 300,
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5',
                  padding: 10,
                  borderRadius: 4,
                  listStyle: 'none',
                  marginTop: 8,
                  marginBottom: 0,
                  /* snap to each diff row */
                  scrollSnapType: 'y mandatory'
                }}
              >
                {mergedDiffs.map((diffKey, i) => (
                  <li
                    key={`${diffKey}-${i}`}
                    onClick={() => handleDiffClick(i)}
                    style={{
                      margin: '4px 0',
                      cursor: 'pointer',
                      // Highlight the active item with a background color
                      backgroundColor: i === selectedIndex ? '#ffe58f' : '#f5f5f5',
                      /* tell the scroller that each diff row is a snap point */
                      scrollSnapAlign: 'start'
                    }}
                  >
                    {diffKey}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Col>
      </Row>
    </>
  );
};
